import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { errorMessage, useTRPC } from "../../lib/api";
import { Button, ErrorText, Section } from "../../ui";
import { LinkDialog } from "./LinkDialog";
import { applyOrder, faviconUrl, groupLinks, hostOf, moveLink, type LinkScope, type LinkTile } from "./links-model";

type DialogState = { link: LinkTile | null } | null;

/** One set of link tiles (a workspace's, or the person's own), grouped, with add/edit/reorder. */
export function LinksSection({ scope, title, description }: { scope: LinkScope; title: string; description: string }) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const key = trpc.dashboard.links.queryKey({ scope });
  const links = useQuery(trpc.dashboard.links.queryOptions({ scope }));
  const [dialog, setDialog] = useState<DialogState>(null);

  const reorder = useMutation(
    trpc.dashboard.reorderLinks.mutationOptions({
      onMutate: async ({ orderedIds }) => {
        await queryClient.cancelQueries({ queryKey: key });
        const previous = queryClient.getQueryData(key);
        if (previous !== undefined) queryClient.setQueryData(key, applyOrder(previous, orderedIds));
        return { previous };
      },
      onError: (_error, _input, context) => {
        if (context?.previous !== undefined) queryClient.setQueryData(key, context.previous);
      },
      onSettled: () => queryClient.invalidateQueries({ queryKey: key }),
    }),
  );

  const all = links.data === undefined ? [] : links.data;
  const groups = groupLinks(all);
  const move = (id: string, direction: "up" | "down") => {
    const orderedIds = moveLink(all, id, direction);
    if (orderedIds !== null) reorder.mutate({ scope, orderedIds });
  };

  return (
    <Section title={title} description={description}>
      <div className="flex flex-col gap-4">
        {links.isPending ? <p className="text-sm text-muted">Loading links…</p> : null}
        {links.isError ? <ErrorText>{errorMessage(links.error)}</ErrorText> : null}
        {reorder.isError ? <ErrorText>{errorMessage(reorder.error)}</ErrorText> : null}
        {links.isSuccess && all.length === 0 ? (
          <p className="text-sm text-muted">No links yet. Add the consoles, docs and tools you open every day.</p>
        ) : null}
        {groups.map((g) => (
          <div key={g.group} className="flex flex-col gap-2">
            <h3 className="text-xs font-medium uppercase tracking-wide text-muted">{g.group}</h3>
            <ul className="grid gap-2 sm:grid-cols-2" aria-label={`${g.group} links`}>
              {g.links.map((l, i) => (
                <LinkTileItem
                  key={l.id}
                  link={l}
                  first={i === 0}
                  last={i === g.links.length - 1}
                  busy={reorder.isPending}
                  onMove={(d) => move(l.id, d)}
                  onEdit={() => setDialog({ link: l })}
                />
              ))}
            </ul>
          </div>
        ))}
        <div>
          <Button onClick={() => setDialog({ link: null })}>Add link</Button>
        </div>
      </div>
      {dialog === null ? null : (
        <LinkDialog
          scope={scope}
          scopeLabel={title}
          link={dialog.link}
          groups={groups.map((g) => g.group)}
          onClose={() => setDialog(null)}
        />
      )}
    </Section>
  );
}

function IconButton({ label, disabled, onClick, path }: { label: string; disabled: boolean; onClick: () => void; path: string }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      // aria-disabled, not disabled: a moved tile keeps keyboard focus even when it reaches an end.
      aria-disabled={disabled}
      onClick={() => {
        if (!disabled) onClick();
      }}
      className="grid size-8 place-items-center rounded-md text-muted hover:bg-surface-2 aria-disabled:opacity-30"
    >
      <svg aria-hidden="true" viewBox="0 0 20 20" className="size-4" fill="currentColor">
        <path d={path} />
      </svg>
    </button>
  );
}

function LinkTileItem({
  link,
  first,
  last,
  busy,
  onMove,
  onEdit,
}: {
  link: LinkTile;
  first: boolean;
  last: boolean;
  busy: boolean;
  onMove: (direction: "up" | "down") => void;
  onEdit: () => void;
}) {
  const host = hostOf(link.url);
  return (
    <li className="flex items-center gap-2 rounded-lg border border-border bg-surface p-2">
      <a
        href={link.url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex min-w-0 flex-1 items-center gap-3 rounded-md p-1 hover:bg-surface-2"
      >
        <img src={faviconUrl(link.url)} alt={`${host} icon`} loading="lazy" width={24} height={24} className="size-6 shrink-0 rounded" />
        <span className="min-w-0">
          <span className="block truncate text-sm font-medium text-text">{link.title}</span>
          <span className="block truncate text-xs text-muted">{host}</span>
        </span>
        <span className="sr-only">(opens in a new tab)</span>
      </a>
      <div className="flex shrink-0 items-center">
        <IconButton label={`Move ${link.title} up`} disabled={first || busy} onClick={() => onMove("up")} path="M10 5l5 6h-4v5H9v-5H5z" />
        <IconButton label={`Move ${link.title} down`} disabled={last || busy} onClick={() => onMove("down")} path="M10 15l-5-6h4V4h2v5h4z" />
        <IconButton
          label={`Edit ${link.title}`}
          disabled={false}
          onClick={onEdit}
          path="M13.6 3.6l2.8 2.8-9 9H4.6v-2.8zM12.2 5l1.4-1.4 2.8 2.8L15 7.8z"
        />
      </div>
    </li>
  );
}
